import { ReactNode } from 'react';
import cn from 'classnames';
import { IconArrow } from 'components/Shared/Icon/themes/BGH-RD3/iconArrow';
import { Button } from 'components/Button';
import accordionStyles from 'components/Accordion/styles.scss';
import styles from './installationContainer.scss';
import { getConfiguratorAddons, getConfiguratorInstallations } from '../selector';
import { hasElements, NO_ELEMENT, rm } from 'utils';
import { IInstallationAddonProduct } from 'models/InstallationConfigurator';
import { ConfiguratorBase } from 'models/ConfiguratorBase';
import { useToggle } from 'hooks';

interface ConfiguratorProps {
  configurator: ConfiguratorBase;
}

interface Props extends ConfiguratorProps {
  children: ReactNode;
  isMobile?: boolean;
  inDrawer?: boolean;
  disabled?: boolean;
  initialOpen?: boolean;
  title: string;
}

const Selection = ({ title }: Pick<IInstallationAddonProduct, 'title'>) => {
  if (!title) {
    return NO_ELEMENT;
  }

  return (
    <p>
      {rm('productpage_installation_choice')} {title}
    </p>
  );
};

interface SelectionPresenterProps {
  isOpen?: boolean;
}

const getSelectedAddons = ({ configurator }: ConfiguratorProps) => {
  const configuratorAddons = getConfiguratorAddons({ configurator });
  return configuratorAddons.filter(addon => addon.selected);
};

const getSelectedInstallations = ({ configurator }: ConfiguratorProps) => {
  const configuratorInstallations = getConfiguratorInstallations({ configurator });
  return configuratorInstallations.filter(installation => installation.selected);
};

const SelectionPresenter = ({
  isOpen,
  configurator
}: SelectionPresenterProps & { configurator: ConfiguratorBase }) => {
  const selectedAddons = getSelectedAddons({ configurator });
  const selectedInstallations = getSelectedInstallations({ configurator });
  const hasSelection = hasElements(selectedAddons) || hasElements(selectedInstallations);

  if (!hasSelection || isOpen) {
    return NO_ELEMENT;
  }

  return (
    <div className={styles.presenterContainer}>
      {selectedAddons.map(({ title }: { title: string }) => (
        <Selection key={title} title={title} />
      ))}
      {selectedInstallations.map(({ title }: { title: string }) => (
        <Selection key={title} title={title} />
      ))}
    </div>
  );
};

const ChangeIcon = ({ isOpen }: SelectionPresenterProps) => {
  if (isOpen) {
    return <IconArrow className={styles.arrow} size="tiny" appearance="flip-270" />;
  }

  return <p className={styles.change}>{rm('checkout_overlay_button')}</p>;
};

export const InstallationContainer = ({
  children,
  isMobile,
  inDrawer,
  disabled,
  initialOpen,
  title,
  configurator
}: Props) => {
  const mobileOrDrawer = isMobile || inDrawer;
  const [isOpen, toggleOpen] = useToggle(initialOpen);

  return (
    <div
      className={cn(styles.installationContainer, {
        [styles.mobile]: mobileOrDrawer,
        [styles.open]: !isOpen
      })}
    >
      <Button
        disabled={disabled}
        onClick={toggleOpen}
        appearance="tertiary"
        className={cn(styles.header, accordionStyles.container)}
      >
        <h3 className={cn(styles.title, accordionStyles.sectionHeader)}>{title}</h3>
        <ChangeIcon isOpen={isOpen} />
      </Button>
      <SelectionPresenter isOpen={isOpen} configurator={configurator} />
      {!disabled && isOpen && <div className={styles.body}>{children}</div>}
    </div>
  );
};
